<template>
  <v-dialog
    :value="value"
    fullscreen
    hide-overlay
    transition="fade-transition"
    @input="$emit('input', $event)"
  >
    <div class="lb-root" @click.self="close">
      <!-- Top bar -->
      <div class="lb-topbar">
        <v-btn icon dark @click="close"><v-icon>mdi-close</v-icon></v-btn>
        <v-icon dark class="ml-2 mr-2">{{
          current && isImage(current) ? "mdi-file-image" : "mdi-file-video"
        }}</v-icon>
        <span class="white--text text-truncate" style="max-width: 40vw">{{
          current ? current.fileName || "attachment" : ""
        }}</span>
        <v-spacer />
        <a
          v-if="current"
          :href="current.url"
          :download="current.fileName || true"
          target="_blank"
          rel="noopener"
          class="text-decoration-none"
        >
          <v-btn color="orange darken-2" dark depressed>
            <v-icon left>mdi-download</v-icon
            >{{ $t("reports.aiCredit.download") }}
          </v-btn>
        </a>
      </div>

      <!-- Nav + preview -->
      <div class="lb-stage">
        <v-btn
          v-if="items.length > 1"
          icon
          x-large
          dark
          class="lb-nav"
          @click="prev"
          ><v-icon size="40">mdi-chevron-left</v-icon></v-btn
        >

        <div class="lb-preview">
          <img
            v-if="current && isImage(current)"
            :src="current.url"
            class="lb-media"
            alt="attachment"
          />
          <video
            v-else-if="current && isVideo(current)"
            :src="current.url"
            class="lb-media"
            controls
            autoplay
          />
          <div v-else-if="current" class="lb-fallback">
            <v-icon size="80" color="grey lighten-1"
              >mdi-file-document-outline</v-icon
            >
            <div class="white--text mt-2">{{ current.fileName }}</div>
          </div>
          <div v-else class="white--text">
            {{ $t("reports.aiCredit.noAttachments") }}
          </div>
        </div>

        <v-btn
          v-if="items.length > 1"
          icon
          x-large
          dark
          class="lb-nav"
          @click="next"
          ><v-icon size="40">mdi-chevron-right</v-icon></v-btn
        >
      </div>

      <!-- Thumbnails -->
      <div v-if="items.length" class="lb-thumbs">
        <div
          v-for="(att, i) in items"
          :key="att.id != null ? att.id : i"
          class="lb-thumb"
          :class="{ 'lb-thumb-active': i === index }"
          @click="index = i"
        >
          <img
            v-if="isImage(att)"
            :src="att.url"
            class="lb-thumb-media"
            alt="thumb"
          />
          <div v-else class="lb-thumb-media lb-thumb-video">
            <v-icon color="white" size="34">mdi-play-circle-outline</v-icon>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
/**
 * Full-screen lightbox for feedback attachments: image/video preview with
 * prev/next navigation, a thumbnail strip, and a Download action. `attachments`
 * is the list; `startIndex` selects the initially-shown item.
 */
export default {
  name: "FeedbackAttachmentDialog",
  props: {
    value: { type: Boolean, default: false },
    attachments: { type: Array, default: () => [] },
    startIndex: { type: Number, default: 0 },
  },
  data() {
    return { index: 0 };
  },
  computed: {
    items() {
      return this.attachments || [];
    },
    current() {
      return this.items[this.index] || null;
    },
  },
  watch: {
    value(open) {
      if (open)
        this.index = Math.min(
          Math.max(this.startIndex, 0),
          Math.max(this.items.length - 1, 0)
        );
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    next() {
      if (this.items.length) this.index = (this.index + 1) % this.items.length;
    },
    prev() {
      if (this.items.length)
        this.index = (this.index - 1 + this.items.length) % this.items.length;
    },
    isImage(att) {
      if (!att) return false;
      if (att.mimeType) return att.mimeType.startsWith("image/");
      return att.fileType === "image";
    },
    isVideo(att) {
      if (!att) return false;
      if (att.mimeType) return att.mimeType.startsWith("video/");
      return att.fileType === "video";
    },
  },
};
</script>

<style scoped>
.lb-root {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  flex-direction: column;
}
.lb-topbar {
  display: flex;
  align-items: center;
  padding: 12px 16px;
}
.lb-stage {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 0 8px;
}
.lb-nav {
  flex: 0 0 auto;
}
.lb-preview {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  height: 100%;
}
.lb-media {
  max-width: min(70vw, 900px);
  max-height: 70vh;
  border-radius: 6px;
  object-fit: contain;
}
.lb-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.lb-thumbs {
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px;
  flex-wrap: wrap;
}
.lb-thumb {
  width: 120px;
  height: 78px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
}
.lb-thumb-active {
  border-color: #f39c12;
}
.lb-thumb-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.lb-thumb-video {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2a2a2a;
}
</style>
